import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { mockPostos } from '../mockPostos';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Zap, MapPin, Info } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix para os ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Criar ícones personalizados baseados no estado
const createCustomIcon = (estado) => {
  let color = '#10b981'; // Verde para Disponível
  
  if (estado === 'Em Uso') {
    color = '#3b82f6'; // Azul
  } else if (estado === 'Manutenção') {
    color = '#f59e0b'; // Laranja
  }
  
  const iconHtml = `
    <div style="
      background-color: ${color};
      width: 30px;
      height: 30px;
      border-radius: 50% 50% 50% 0;
      border: 3px solid white;
      transform: rotate(-45deg);
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    ">
      <div style="
        transform: rotate(45deg);
        margin-top: 3px;
        margin-left: 3px;
        color: white;
        font-size: 16px;
        font-weight: bold;
      ">⚡</div>
    </div>
  `;
  
  return L.divIcon({
    html: iconHtml,
    className: 'custom-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
  });
};

const MapaPostos = () => {
  const navigate = useNavigate();
  const [selectedLocal, setSelectedLocal] = useState('all');
  
  const locais = ['Pragal', 'Santa Apolónia', 'Évora', 'Freixo'];
  
  const filteredPostos = selectedLocal === 'all' 
    ? mockPostos 
    : mockPostos.filter(p => p.local === selectedLocal);
  
  // Centro de Portugal
  const centerPosition = [39.5, -8.0];
  
  const getEstadoBadge = (estado) => {
    const variants = {
      'Disponível': 'bg-green-100 text-green-800 border-green-200',
      'Em Uso': 'bg-blue-100 text-blue-800 border-blue-200',
      'Manutenção': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return variants[estado] || 'bg-gray-100 text-gray-800 border-gray-200';
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mapa de Postos</h1>
        <p className="text-gray-600 mt-1">Localização geográfica e estado dos postos de carregamento</p>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          variant={selectedLocal === 'all' ? 'default' : 'outline'}
          onClick={() => setSelectedLocal('all')}
        >
          Todos os Locais
        </Button>
        {locais.map(local => (
          <Button
            key={local}
            variant={selectedLocal === local ? 'default' : 'outline'}
            onClick={() => setSelectedLocal(local)}
          >
            {local}
          </Button>
        ))}
      </div>

      {/* Legenda */}
      <Card className="p-4">
        <div className="flex items-center gap-6 flex-wrap">
          <span className="text-sm font-semibold text-gray-700">Legenda:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span className="text-sm text-gray-600">Disponível</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span className="text-sm text-gray-600">Em Uso</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
            <span className="text-sm text-gray-600">Manutenção</span>
          </div>
        </div>
      </Card>

      {/* Mapa */}
      <Card className="overflow-hidden">
        <div style={{ height: '600px', width: '100%' }}>
          <MapContainer
            center={centerPosition}
            zoom={7}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {filteredPostos.map(posto => (
              <Marker
                key={posto.id}
                position={posto.coordenadas}
                icon={createCustomIcon(posto.estado)}
              >
                <Popup>
                  <div className="p-2" style={{ minWidth: '200px' }}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{posto.nome}</h3>
                        <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                          <MapPin className="h-3 w-3" />
                          {posto.local}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <Badge className={getEstadoBadge(posto.estado)}>
                        {posto.estado}
                      </Badge>
                    </div>
                    
                    <div className="space-y-1 mb-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tipo:</span>
                        <span className="font-semibold">{posto.tipo}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Potência:</span>
                        <div className="flex items-center gap-1">
                          <Zap className="h-3 w-3 text-yellow-600" />
                          <span className="font-semibold">{posto.potencia} kW</span>
                        </div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Utilização:</span>
                        <span className="font-semibold">{posto.utilizacaoMensal}%</span>
                      </div>
                    </div>
                    
                    <Button 
                      size="sm" 
                      className="w-full"
                      onClick={() => navigate(`/carregamento/postos/${posto.id}`)}
                    >
                      <Info className="h-3 w-3 mr-2" />
                      Ver Detalhes
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </Card>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-600 mb-1">Total de Postos</p>
          <p className="text-3xl font-bold text-gray-900">{filteredPostos.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-600 mb-1">Disponíveis</p>
          <p className="text-3xl font-bold text-green-600">
            {filteredPostos.filter(p => p.estado === 'Disponível').length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-600 mb-1">Em Uso / Manutenção</p>
          <p className="text-3xl font-bold text-blue-600">
            {filteredPostos.filter(p => p.estado !== 'Disponível').length}
          </p>
        </Card>
      </div>
    </div>
  );
};

export default MapaPostos;
